import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useGetCustomFields } from "../../queries/customFields";
import {
  CustomField,
  CustomFieldValues,
  UdfDataType,
  UdfEntityType,
} from "../../types/CustomFields";
import { Input, Label } from "./InputField";
import { Select } from "./Select";

interface CustomFieldsSectionProps {
  entityType: UdfEntityType;
  values: CustomFieldValues;
  onChange: (values: CustomFieldValues) => void;
  disabled?: boolean;
  embedded?: boolean;
}

function CustomFieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: CustomField;
  value: any;
  onChange: (val: any) => void;
  disabled?: boolean;
}) {
  if (field.dataType === UdfDataType.DROPDOWN) {
    return (
      <Select
        options={field.valueSet ?? []}
        placeholder={field.placeholder || `Select ${field.name}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        required={field.isRequired}
      />
    );
  }

  if (field.dataType === UdfDataType.DATE) {
    return (
      <Input
        type="date"
        value={value ?? ""}
        min={field.validations?.dateMin}
        max={field.validations?.dateMax}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        required={field.isRequired}
      />
    );
  }

  if (field.dataType === UdfDataType.NUMBER) {
    return (
      <Input
        type="number"
        placeholder={field.placeholder || ""}
        value={value ?? ""}
        min={field.validations?.min}
        max={field.validations?.max}
        onChange={(e) =>
          onChange(e.target.value !== "" ? parseFloat(e.target.value) : null)
        }
        disabled={disabled}
        required={field.isRequired}
      />
    );
  }

  return (
    <Input
      type="text"
      placeholder={field.placeholder || ""}
      value={value ?? ""}
      minLength={field.validations?.min}
      maxLength={field.validations?.max}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      required={field.isRequired}
    />
  );
}

export function CustomFieldsSection({
  entityType,
  values,
  onChange,
  disabled = false,
  embedded = false,
}: CustomFieldsSectionProps) {
  const { data: fields = [] } = useGetCustomFields(entityType);
  const hasExistingValues = Object.values(values).some(
    (v) => v !== null && v !== undefined && v !== ""
  );
  const [expanded, setExpanded] = useState(hasExistingValues);

  useEffect(() => {
    if (fields.length > 0) {
      setExpanded(true);
    }
  }, [fields]);

  if (fields.length === 0) return null;

  const handleChange = (fieldPath: string, val: any) => {
    onChange({ ...values, [fieldPath]: val });
  };

  const fieldsGrid = (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {fields.map((field) => (
        <div key={field.id} className="flex flex-col gap-1.5">
          <Label className="text-sm font-normal">
            {field.name}
            {field.isRequired && <span className="ml-0.5 text-red-500">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-light">{field.description}</p>
          )}
          <CustomFieldInput
            field={field}
            value={values[field.fieldPath] ?? null}
            onChange={(val) => handleChange(field.fieldPath, val)}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );

  if (embedded) return fieldsGrid;

  return (
    <div className="border rounded-lg overflow-hidden mt-2">
      {/* Collapsible header */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-sm font-medium text-gray-700">
          Custom Fields{" "}
          <span className="text-gray-400 font-normal">({fields.length})</span>
        </span>
        <Icon
          icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="w-4 h-4 text-gray-500"
        />
      </button>

      {expanded && <div className="bg-white p-4">{fieldsGrid}</div>}
    </div>
  );
}

export default CustomFieldsSection;
